import {
  Component,
  OnInit,
  HostListener,
  ElementRef,
  QueryList,
  ViewChildren,
  ChangeDetectorRef,
  Input,
  ChangeDetectionStrategy,
  Output,
  EventEmitter,
} from '@angular/core';
import formSchema from '../user-profile/form-json-schema/form-schema.json';
import {
  UserRegistration,
  Schema,
  UserProfileFormInterface,
  Validator,
} from './model/model.userProfileForm';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { stateOrCountryDropdownAnimation } from 'src/app/shared/reusable-animations/card-estimate-drawer-animations';
import { statesAllowedIso3Countries } from 'src/app/config/app-config';
import {
  Subscription,
} from 'rxjs';
import { StringUtils } from 'src/app/shared/utils/string-utils';
import { config as countryConfig } from 'src/app/config/country-config';
import { parse, differenceInYears } from 'date-fns';
import { Store } from '@ngrx/store';
import { defaultCountry, isUpdateIdEnabled } from 'src/app/store/service-options';

@Component({
    selector: 'app-user-profile',
    templateUrl: './user-profile.component.html',
    styleUrls: ['./user-profile.component.scss'],
    animations: [stateOrCountryDropdownAnimation],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class UserProfileComponent implements OnInit {
  @Output() notifyUserRegistration: EventEmitter<UserRegistration> = new EventEmitter<UserRegistration>();
  private _countries?: any

  private _cmsLabels: any
  @Input() public set cmsLabels(labels: any) { // Todo: Tim: fix types
    this._cmsLabels = labels
    const idTypeLabels = this.cmsLabels.stellar.enumsObj.PERSONAL_ID1_TYPE
    this.idOptions = this.updateIdLabelTranslations(idTypeLabels, this.idOptions)
  }
  public get cmsLabels(): any { // Todo: Tim: fix types
    return this._cmsLabels
  } 

  @Input() public set countries(countries: any) {
    if (countries === null) {
      this._countries = this.convertCountryConfigToCountries(countryConfig);
    }
    else {
      this._countries = countries;
    }
  }

  public get countries(): any {
    return this._countries;
  }

  private currentIsUpdateIdEnabled: boolean = false;
  private currentDefaultCountry: string = '';

  public getIdOptions(): any[] {
    if (this.currentIsUpdateIdEnabled && this.currentDefaultCountry === 'ARG') {
      return [
        {
          code: "GOV",
          name: "Government ID"
        },
        {
          code: "PAS",
          name: "Passport"
        },
        {
          code: "ALN",
          name: "Alien ID"
        }
      ]
    } else if (this.currentIsUpdateIdEnabled && this.currentDefaultCountry !== 'USA') { // #if featureFlag is true and country is not USA
      return [
        {
          code: "GOV",
          name: `Government ID`
      },
      {
          code: "PAS",
          name: "Passport"
      }
      ]
    } else {
       return [ // #if featureFlag is false or the country is
        {
            code: "ALN",
            name: "Alien ID"
        },
        {
            code: "DRV",
            name: "Drivers License"
        },
        {
            code: "GOV",
            name: "Government ID"
        },
        {
            code: "PAS",
            name: "Passport"
        },
        {
            code: "STA",
            name: "State ID"
        }
    ]
  }}

  // Todo: Tim: These are USA only option. Hardcoding for initial launch of Digital Payment, but later these options will be pulled dynamically from the enumerations API based on the selected country
  public idOptions = this.getIdOptions();

  private states = []
  private idStates = []

  isStateFieldDisabled: boolean = true
  isIdStateFieldDisabled: boolean = true
  userProfileSchema: UserProfileFormInterface = formSchema as UserProfileFormInterface
  formGroup: FormGroup
  activeDropdown: string | null = null

  countryControlSub: Subscription | undefined
  idTypeSub: Subscription | undefined
  idCountrySub: Subscription | undefined
  private dateOfBirthSubscription: Subscription | undefined
  private defaultCountrySub: Subscription | undefined
  private isUpdateIdEnabledSub: Subscription | undefined

  @ViewChildren('inputContainer') inputContainerElement: QueryList<ElementRef>
  @HostListener('document:click', ['$event']) onClickOutside(event: MouseEvent) {
    const targetElement = event.target as HTMLElement;
    if (this.inputContainerElement) {
      let clickedInside = false

      this.inputContainerElement.forEach((container) => {
        if (container.nativeElement.contains(targetElement)) {
          clickedInside = true
        }
      })

      if (!clickedInside) {
        this.activeDropdown = null
      }
    }
  }
  constructor(private fb: FormBuilder, private cdr: ChangeDetectorRef, private store: Store) {}
  ngOnInit(): void {
    // Subscribe to defaultCountry selector
    this.defaultCountrySub = this.store.select(defaultCountry).subscribe(country => {
      this.currentDefaultCountry = country;
      // Update ID options when default country changes
      this.idOptions = this.getIdOptions();
      this.cdr.markForCheck();
    });

    // Subscribe to isUpdateIdEnabled selector
    this.isUpdateIdEnabledSub = this.store.select(isUpdateIdEnabled).subscribe(enabled => {
      this.currentIsUpdateIdEnabled = enabled;
      // Update ID options when feature flag changes
      this.idOptions = this.getIdOptions();
      this.cdr.markForCheck();
    });

    this.formGroup = this.buildForm(this.userProfileSchema.schemas);
    // TODO: integrate google location lookup
    // this.locationLookupInput.valueChanges.subscribe(
    //   (value: string | null | undefined) => {
        
    //   }
    // )

    if (this.isStateFieldDisabled) {
      this.disableStateField('state')
    } else {
      this.formGroup.get('state')?.enable()
    }
    if (this.isIdStateFieldDisabled) {
      this.disableStateField('senderPrimaryIdentificationIssueCountrySubdivisionCode')
    } else {
      this.formGroup.get('senderPrimaryIdentificationIssueCountrySubdivisionCode')?.enable()
    }

    this.countryControlSub = this.formGroup.get('country')?.valueChanges.subscribe((value) => {
      if (statesAllowedIso3Countries.includes(value)) {
        this.enableStateField('state')
        const country = this.countries.find((country: any) => country.code === value)
        this.states = country.states ?? []
      }
      else {
        this.states = []
        this.disableStateField('state')
      }
      this.resetStateController('state')
      
      // Update ID options when country changes
      this.idOptions = this.getIdOptions()
      this.cdr.markForCheck()
    })

    this.subscribeToIdFields()
    
    // Update ID options after form is built
    this.idOptions = this.getIdOptions()
  }

  ngOnDestroy() {
    if (this.countryControlSub) { this.countryControlSub.unsubscribe() }
    if (this.idTypeSub) { this.idTypeSub.unsubscribe() }
    if (this.idCountrySub) { this.idCountrySub.unsubscribe() }
    if (this.dateOfBirthSubscription) { this.dateOfBirthSubscription.unsubscribe() }
    if (this.defaultCountrySub) { this.defaultCountrySub.unsubscribe() }
    if (this.isUpdateIdEnabledSub) { this.isUpdateIdEnabledSub.unsubscribe() }
  }

  private resetStateController(stateFieldName: string) {
    this.formGroup.get(stateFieldName)?.setValue('')
  }

  private subscribeToIdFields(): void {
    this.idTypeSub = this.formGroup.get('senderPrimaryIdentificationTypeCode')?.valueChanges.subscribe(value => {
      this.updateIdStateFormControl('senderPrimaryIdentificationIssueCountrySubdivisionCode')
    })

    this.idCountrySub = this.formGroup.get('senderPrimaryIdentificationIssueCountryCode')?.valueChanges.subscribe(value => {
      this.updateIdStateFormControl('senderPrimaryIdentificationIssueCountrySubdivisionCode')
      
      // Update ID options when ID country changes
      this.idOptions = this.getIdOptions()
      this.cdr.markForCheck()
    })
  }

  private updateIdStateFormControl(stateFieldName: string): void {
    const idType = this.formGroup.get('senderPrimaryIdentificationTypeCode')?.value
    const idCountry = this.formGroup.get('senderPrimaryIdentificationIssueCountryCode')?.value

    if (statesAllowedIso3Countries.includes(idCountry) && ['DRV', 'STA'].includes(idType)) {
      this.enableStateField('senderPrimaryIdentificationIssueCountrySubdivisionCode')
      const country = this.countries.find((country: any) => country.code === idCountry)
      this.idStates = country.states ?? []
    }
    else {
      this.idStates = []
      this.disableStateField('senderPrimaryIdentificationIssueCountrySubdivisionCode')
    }
    this.resetStateController('senderPrimaryIdentificationIssueCountrySubdivisionCode')
  }

  private disableStateField(fieldName: string) {
    if (fieldName === 'state') {
      this.formGroup.get('state')?.setValue('')
      this.formGroup.get('state')?.disable()
    }
    else if (fieldName === 'senderPrimaryIdentificationIssueCountrySubdivisionCode') {
      this.formGroup.get('senderPrimaryIdentificationIssueCountrySubdivisionCode')?.setValue('')
      this.formGroup.get('senderPrimaryIdentificationIssueCountrySubdivisionCode')?.disable()
    }
  }

  private enableStateField(fieldName: string) {
    if (fieldName === 'state') {
      this.formGroup.get('state')?.enable()
    }
    else if (fieldName === 'senderPrimaryIdentificationIssueCountrySubdivisionCode') {
      this.formGroup.get('senderPrimaryIdentificationIssueCountrySubdivisionCode')?.enable()
    }
  }

  private buildForm(schemas: Schema[]): FormGroup {
    const group: { [key: string]: any } = {};
    schemas.forEach((field) => {
      if (field.type === 'object' && field.properties) {
        group[field.name] = this.buildForm(Object.values(field.properties));
      } else {
        group[field.name] = new FormControl(field.value || '', {
          validators: this.mapValidators(field.validators),
          updateOn: 'change',
        });
      }
    });
    
    const formGroup = this.fb.group(group);
    
    // Add age validation for dateOfBirth
    if (formGroup.get('dateOfBirth')) {
      const dateOfBirthGroup = formGroup.get('dateOfBirth') as FormGroup;
      
      // Store the subscription
      this.dateOfBirthSubscription = dateOfBirthGroup.valueChanges.subscribe(() => {
        this.validateAge(formGroup);
        this.cdr.markForCheck();
      });
    }
    
    return formGroup;
  }

  private mapValidators(validatorsObj: Validator | undefined): any[] {
    const validator: any[] = [];
    if (!validatorsObj) return validator;
    if (validatorsObj.required) {
      validator.push(Validators.required);
    }
    if (typeof validatorsObj.minLength === 'number') {
      validator.push(Validators.minLength(validatorsObj.minLength));
    }
    if (typeof validatorsObj.maxLength === 'number') {
      validator.push(Validators.maxLength(validatorsObj.maxLength));
    }
    if (validatorsObj.pattern) {
      validator.push(Validators.pattern(validatorsObj.pattern));
    }
    return validator;
  }

  objectToArray(obj: { [key: string]: Schema } | undefined): Schema[] {
    return Object.keys(obj!).map((key) => obj![key]);
  }

  // Todo: Tim: this label updating logic is hardcoded for the initial launch of digital on/off-ramps. Will be updated later when we pull these fields dynamically
  updateIdLabelTranslations(idTypeLabels: any, optionsArray: { code: string, name: string}[]) { // Todo: Fix types
    const updatedIdOptions = optionsArray.map(option => {
      return {
        code: option.code,
        name: idTypeLabels[option.code] ?? option.name
      }
    })
    return updatedIdOptions
  }

  getAsteriskClass(fieldName: string): boolean {
    const control = this.formGroup.get(fieldName)
    if (!control) return false

    const monthControl = control.get('month')
    const dayControl = control.get('day')
    const yearControl = control.get('year')
    const isInvalidAndTouchedOrDirty = (ctrl: any) =>
      ctrl && ctrl.invalid && (ctrl.touched || ctrl.dirty)

    const monthHasRequiredError =
      monthControl?.errors?.required &&
      isInvalidAndTouchedOrDirty(monthControl);
    const dayHasRequiredError =
      dayControl?.errors?.required && isInvalidAndTouchedOrDirty(dayControl)
    const yearHasRequiredError =
      yearControl?.errors?.required && isInvalidAndTouchedOrDirty(yearControl)

    if (monthHasRequiredError || dayHasRequiredError || yearHasRequiredError) {
      return true;
    }
    return false;
  }

  setActiveDropdown(value: string | null) {
    console.log('setActiveDropdown', value) // todo: remove:
    this.activeDropdown = this.activeDropdown === value ? null : value;
  }

  private updatedFormObject(formValue: UserRegistration): UserRegistration {
    const updatedForm = { ...formValue };
    const countries = [...this.countries];

    const country = countries.find(
      (c) =>
        StringUtils.removeDiacriticsAndConvertToLowercase(c.name) ===
        StringUtils.removeDiacriticsAndConvertToLowercase(formValue?.country)
    );

    if (country) {
      updatedForm.country = country.code || ''; // TODO: Tim: had to add an || '' when implementing the countries from API call. Need to double check that this isn't breaking the form

      const state = country.states?.find(
        (state: any) => // Todo: fix types
          StringUtils.removeDiacriticsAndConvertToLowercase(state.name) ===
          StringUtils.removeDiacriticsAndConvertToLowercase(formValue.state)
      );

      if (state) {
        updatedForm.state = state.code;
      }
    }

    const countryOfBirth = countries.find(
      (c) =>
        StringUtils.removeDiacriticsAndConvertToLowercase(c.name) ===
        StringUtils.removeDiacriticsAndConvertToLowercase(
          formValue.countryOfBirth
        )
    );

    if (countryOfBirth) {
      updatedForm.countryOfBirth = countryOfBirth.code || ''; // TODO: Tim: had to add an || '' when implementing the countries from API call. Need to double check that this isn't breaking the form
    }

    return updatedForm;
  }

  convertCountryConfigToCountries(countriesConfig: any): any[] {
    let countries: any[] = [];
    
    countriesConfig.forEach((country: any) => {
      let countryObj: any = {
        iso2code: country.iso2Code,
        name: country.name,
        code: country.code,
      }
      if (!!country.states[0]) {
        countryObj['states'] = country.states;
      }
      
      countries.push(countryObj);
    })

    return countries;
  }

  onSubmit(formValue: UserRegistration) {
    if (this.formGroup.valid) {
      this.notifyUserRegistration.emit(this.updatedFormObject(formValue));
    } else {
      Object.keys(this.formGroup.controls).forEach((field) => {
        const control = this.formGroup.get(field);
        control?.markAsTouched({ onlySelf: true });
      });
    }
  }

  public getFieldLabel(labelId: string): string {
    if (
      labelId === 'senderPrimaryIdentificationTypeCode' ||
      labelId === 'senderPrimaryIdentificationId' ||
      labelId === 'senderPrimaryIdentificationIssueCountryCode' ||
      labelId === 'senderPrimaryIdentificationIssueCountrySubdivisionCode'
    ) {
      return this.cmsLabels.stellar.gffp.gffpLabels[labelId] || labelId
    }
    return this.cmsLabels.stellar.signIn.userProfile[labelId] || labelId
  }

  public getDropdownOptions(fieldName: string): any[] {
    if (['country', 'countryOfBirth', 'senderPrimaryIdentificationIssueCountryCode'].includes(fieldName)) return this.countries
    else if (fieldName === 'state') return this.states
    else if (fieldName === 'senderPrimaryIdentificationIssueCountrySubdivisionCode') return this.idStates
    else if (fieldName === 'senderPrimaryIdentificationTypeCode') return this.idOptions
    else {
      return []
    }
  }

  private validateAge(formGroup: FormGroup): void {
    const dateOfBirthGroup = formGroup.get('dateOfBirth');
    if (!dateOfBirthGroup) return;
    
    const month = dateOfBirthGroup.get('month')?.value;
    const day = dateOfBirthGroup.get('day')?.value;
    const year = dateOfBirthGroup.get('year')?.value;
    
    if (month && month.length === 2 && day && day.length === 2 && year && year.length === 4) {
      try {
        const dateString = `${year}-${month}-${day}`;
        const date = parse(dateString, 'yyyy-MM-dd', new Date());
        const age = differenceInYears(new Date(), date);
        
        if (age < 18) {
          dateOfBirthGroup.setErrors({ minAge: true });
        } 
        else if (age >= 101) {
          dateOfBirthGroup.setErrors({ maxAge: true });
        } 
        else if (!age) {
          dateOfBirthGroup.setErrors({ invalidDate: true });
        }
        else {
          // Clear age-related errors but preserve other validation errors
          const currentErrors = dateOfBirthGroup.errors;
          if (currentErrors) {
            const { minAge, maxAge, ...otherErrors } = currentErrors;
            dateOfBirthGroup.setErrors(Object.keys(otherErrors).length ? otherErrors : null);
          }
        }
      } catch (e) {
        // Invalid date format
        dateOfBirthGroup.setErrors({ invalidDate: true });
      }
    }
  }
}
